import { useEffect, useState } from 'react';

/**
 * Events the HTML spec counts as "activation triggering input events". On touch, activation is
 * only granted once the tap completes, hence `touchend`/`pointerup` rather than their `start`/
 * `down` counterparts. Scrolling and wheel are deliberately absent: no browser treats them as
 * activating gestures, so they can never unblock audio.
 */
export const ACTIVATION_EVENTS = ['keydown', 'mousedown', 'pointerup', 'touchend'] as const;

const STORAGE_KEY = 'specify:user-activated';

let hasActivated = false;
let isListening = false;
const subscribers = new Set<() => void>();

const readStoredActivation = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false; //Safari private mode and friends
  }
};

const storeActivation = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    //not being able to remember the gesture only costs us autoplay after a hard reload
  }
};

const stopListening = () => {
  ACTIVATION_EVENTS.forEach((event) => window.removeEventListener(event, markActivated, true));
  isListening = false;
};

function markActivated() {
  if (hasActivated) return;

  hasActivated = true;
  storeActivation();

  if (isListening) stopListening();

  subscribers.forEach((notify) => notify());
}

/**
 * Latches "the visitor has interacted with the site" for the rest of the session.
 *
 * Two sources feed it:
 *  - the module-level flag, set by the listeners below. It covers client-side (Next `Link`)
 *    navigation for free, because the route change keeps the same document and therefore the same
 *    module instance: a click on the previous page is still remembered on this one.
 *  - a `sessionStorage` flag, which additionally survives a *hard* navigation. The browser resets
 *    its own activation on a fresh document, so this is a hint rather than a guarantee - callers
 *    must handle a rejected unmuted `play()` by falling back to muted playback.
 *
 * Deliberately NOT fed by `navigator.userActivation.hasBeenActive`: Chrome seeds sticky activation
 * from the navigation that created the document, so it reads true on a cold landing where the
 * visitor has not touched anything yet - the one case that must not count as interaction.
 */
const startListening = () => {
  if (hasActivated || isListening || typeof window === 'undefined') return;

  if (readStoredActivation()) {
    markActivated();
    return;
  }

  //capture phase, so a handler that stops propagation somewhere in the tree cannot hide the gesture
  ACTIVATION_EVENTS.forEach((event) => window.addEventListener(event, markActivated, { capture: true, passive: true }));
  isListening = true;
};

/**
 * Begins recording gestures for the rest of the session.
 *
 * Mounted once at the app root rather than left to the video components, because a click only
 * counts if something was listening when it happened: a visitor who clicks around the home page -
 * which renders no video at all - and then navigates to a page that does would otherwise arrive
 * with nothing recorded, and the video would sit paused despite the interaction.
 */
export const startUserActivationTracking = () => startListening();

/** Imperative read of the same signal, for code that cannot wait for a re-render. */
export const hasUserActivated = () => {
  startListening();
  return hasActivated;
};

/** `true` once the visitor has interacted with the site. Always `false` on the server / first render. */
export const useUserActivation = () => {
  const [isActivated, setIsActivated] = useState(false);

  useEffect(() => {
    startListening();

    if (hasActivated) {
      setIsActivated(true);
      return;
    }

    const notify = () => setIsActivated(true);
    subscribers.add(notify);

    return () => {
      subscribers.delete(notify);
    };
  }, []);

  return isActivated;
};

/**
 * Runs `callback` on every gesture until the returned detach is called.
 *
 * Preferred over `useUserActivation` for anything that calls `HTMLMediaElement.play()`, because
 * Safari (iOS especially) wants the call to happen *inside* the gesture's own handler rather than
 * a React state update later - and because a `play()` the browser still refuses gets another go on
 * the next gesture, which a one-shot latch cannot offer.
 */
export const onUserGesture = (callback: () => void) => {
  if (typeof window === 'undefined') return () => undefined;

  const handle = () => callback();

  ACTIVATION_EVENTS.forEach((event) => window.addEventListener(event, handle, { capture: true, passive: true }));

  return () => ACTIVATION_EVENTS.forEach((event) => window.removeEventListener(event, handle, true));
};
